import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Building2, Plane, Bot, Phone, Users, TrendingDown, Zap, DollarSign, Clock, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const fadeUp = {
 initial: { opacity: 0, y: 20 },
 animate: { opacity: 1, y: 0 },
 transition: { duration: 0.5 },
};

const caseStudies = [
 {
  id: "construction-software",
  icon: Building2,
  industry: "Construction Tech",
  title: "Enterprise Construction Management Platform",
  subtitle: "Competing with Autodesk",
  challenge: "A founder had a vision for construction management software that could compete with industry giants like Autodesk. He needed to validate the market, build a complex product, and acquire initial paying customers, without a technical team.",
  solution: "We partnered with the founder from day one. Validated demand through customer interviews with construction firms, built a comprehensive platform with project management, resource allocation, and compliance tracking. Then executed go-to-market to land the first paying customers.",
  outcomes: [
   { metric: "Early Clarity", description: "Validated market fit before heavy investment" },
   { metric: "Complex Build", description: "Enterprise-grade construction platform" },
   { metric: "Paying Customers", description: "Acquired initial customers through targeted outreach" },
   { metric: "Seed-Ready", description: "Product ready for next stage of funding" },
  ],
  tags: ["Enterprise SaaS", "Construction", "Market Validation", "MVP to Scale"],
 },
 {
  id: "skydiving-erp",
  icon: Plane,
  industry: "Aviation / Adventure Sports",
  title: "Niche ERP Solution for Skydiving Operations",
  subtitle: "From Cost Reduction to Productization",
  challenge: "A skydiving business owner was paying exorbitant yearly subscription fees for generic ERP software that didn't fit his niche operations. He needed a custom solution that could later be productized and sold to other skydiving centers globally.",
  solution: "We built a tailored ERP solution covering scheduling, customer management, equipment tracking, safety compliance, and financials, all specific to skydiving operations. Eliminated his dependency on expensive third-party software and positioned the product for licensing to other operators.",
  outcomes: [
   { metric: "Cost Eliminated", description: "Replaced expensive yearly subscriptions" },
   { metric: "Niche Fit", description: "Built for skydiving-specific workflows" },
   { metric: "Productization", description: "Now scaling as a licensable product" },
   { metric: "Revenue Stream", description: "Turned internal tool into new business line" },
  ],
  tags: ["ERP", "Niche Market", "Cost Reduction", "Productization"],
 },
 {
  id: "ai-orchestration",
  icon: Bot,
  industry: "Internal Operations",
  title: "AI-Powered Business Orchestration Platform",
  subtitle: "Built for Let's Build Startup",
  challenge: "As a growing operation working with multiple founders, we needed a system that could handle payroll, project management, CRM, invoicing, and capacity planning, without manual overhead. We wanted to focus on building, not administrating.",
  solution: "We built our own AI orchestration platform that automates the busywork. Invoices are raised automatically when work is completed. When we have bandwidth, the system helps close deals with new founders. When team members are unwell, it adjusts timelines. When finances hit a bottleneck, it alerts us a quarter in advance.",
  outcomes: [
   { metric: "Auto-Invoicing", description: "Invoices raised automatically on completion" },
   { metric: "Deal Flow", description: "AI assists in closing deals when capacity opens" },
   { metric: "Timeline Management", description: "Auto-adjusts when team availability changes" },
   { metric: "Financial Foresight", description: "Alerts on bottlenecks a quarter ahead" },
  ],
  tags: ["AI Automation", "Operations", "Internal Tool", "Orchestration"],
  isInternal: true,
 },
 {
  id: "ai-onboarding",
  icon: Phone,
  industry: "Customer Operations",
  title: "AI Customer Onboarding & Calling System",
  subtitle: "200 People → 20 People",
  challenge: "A founder was running a global customer onboarding operation with a team of 200 people. The cost was unsustainable, quality was inconsistent, and scaling meant more headcount. He needed a radical efficiency improvement.",
  solution: "We built an AI-powered calling and onboarding system using Retell, Vapi, Zoom, and HeyGen. The system handles customer calls with human-like interactions, customers don't realize they're speaking to AI. Video onboarding sessions look like real human representatives.",
  outcomes: [
   { metric: "90% Team Reduction", description: "From 200 people to 20 people" },
   { metric: "24/7 Operations", description: "AI handles calls around the clock" },
   { metric: "Consistent Quality", description: "No variance in customer experience" },
   { metric: "Human-Like", description: "Customers perceive interactions as human" },
  ],
  tags: ["AI Voice", "Automation", "Cost Reduction", "Customer Experience"],
  highlight: true,
 },
];

export default function CaseStudiesPage() {
 return (
  <div className="flex flex-col">
   {/* Hero */}
   <section className="bg-hero py-16 sm:py-20 lg:py-24">
    <div className="container-narrow">
     <motion.div
      initial="initial"
      animate="animate"
      variants={fadeUp}
      className="text-center"
     >
      <h1 className="heading-display text-foreground mb-6">
       Case Studies
      </h1>
      <p className="body-large text-muted-foreground max-w-2xl mx-auto">
       Real products. Real customers. Real outcomes. Here's what we've built with founders like you.
      </p>
     </motion.div>
    </div>
   </section>

   {/* Stats Bar */}
   <section className="bg-primary text-primary-foreground py-8">
    <div className="container-wide">
     <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
      <div>
       <p className="text-3xl font-serif font-medium">4+</p>
       <p className="text-sm text-primary-foreground/70">Products Built</p>
      </div>
      <div>
       <p className="text-3xl font-serif font-medium">90%</p>
       <p className="text-sm text-primary-foreground/70">Max Team Reduction Achieved</p>
      </div>
      <div>
       <p className="text-3xl font-serif font-medium">Enterprise</p>
       <p className="text-sm text-primary-foreground/70">Grade Solutions</p>
      </div>
      <div>
       <p className="text-3xl font-serif font-medium">Seed-Ready</p>
       <p className="text-sm text-primary-foreground/70">Handover Quality</p>
      </div>
     </div>
    </div>
   </section>

   {/* Case Studies */}
   <section className="bg-background py-16 sm:py-20 lg:py-24">
    <div className="container-wide">
     <div className="space-y-16">
      {caseStudies.map((study, index) => (
       <motion.div
        key={study.id}
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: index * 0.1 }}
       >
        <Card className={`overflow-hidden ${study.highlight ? 'border-accent/50 bg-accent/5' : ''}`}>
         <CardContent className="p-0">
          <div className="grid lg:grid-cols-3 gap-0">
           {/* Left Column - Overview */}
           <div className="p-8 lg:p-10 bg-section border-b lg:border-b-0 lg:border-r border-border/50">
            <div className="flex items-center gap-3 mb-6">
             <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
              <study.icon className="h-6 w-6 text-accent" />
             </div>
             {study.isInternal && (
              <Badge variant="secondary" className="text-xs">Internal Project</Badge>
             )}
             {study.highlight && (
              <Badge className="bg-accent text-accent-foreground text-xs">Featured</Badge>
             )}
            </div>
            
            <Badge variant="outline" className="mb-4">{study.industry}</Badge>
            
            <h2 className="font-serif text-2xl font-medium text-foreground mb-2">
             {study.title}
            </h2>
            <p className="text-accent font-medium mb-4">{study.subtitle}</p>
            
            <div className="flex flex-wrap gap-2 mt-6">
             {study.tags.map((tag) => (
              <Badge key={tag} variant="secondary" className="text-xs">
               {tag}
              </Badge>
             ))}
            </div>
           </div>

           {/* Right Column - Details */}
           <div className="lg:col-span-2 p-8 lg:p-10">
            {/* Challenge */}
            <div className="mb-8">
             <h3 className="text-sm font-semibold text-accent uppercase tracking-wide mb-3">
              The Challenge
             </h3>
             <p className="text-muted-foreground leading-relaxed">
              {study.challenge}
             </p>
            </div>

            {/* Solution */}
            <div className="mb-8">
             <h3 className="text-sm font-semibold text-accent uppercase tracking-wide mb-3">
              What We Built
             </h3>
             <p className="text-muted-foreground leading-relaxed">
              {study.solution}
             </p>
            </div>

            {/* Outcomes */}
            <div>
             <h3 className="text-sm font-semibold text-accent uppercase tracking-wide mb-4">
              Outcomes
             </h3>
             <div className="grid sm:grid-cols-2 gap-4">
              {study.outcomes.map((outcome, i) => (
               <div 
                key={i}
                className="flex items-start gap-3 p-4 bg-section rounded-lg"
               >
                <CheckCircle2 className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                <div>
                 <p className="font-medium text-foreground">{outcome.metric}</p>
                 <p className="text-sm text-muted-foreground">{outcome.description}</p>
                </div>
               </div>
              ))}
             </div>
            </div>
           </div>
          </div>
         </CardContent>
        </Card>
       </motion.div>
      ))}
     </div>
    </div>
   </section>

   {/* What This Means For You */}
   <section className="bg-section py-16 sm:py-20 lg:py-24">
    <div className="container-narrow">
     <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="text-center mb-12"
     >
      <h2 className="heading-large text-foreground mb-4">What This Means For You</h2>
      <p className="body-base text-muted-foreground max-w-2xl mx-auto">
       These aren't just portfolio pieces. They're proof that we build real products that solve real problems.
      </p>
     </motion.div>

     <div className="grid md:grid-cols-3 gap-6">
      {[
       {
        icon: Zap,
        title: "We Build Complex Systems",
        description: "From enterprise SaaS to AI orchestration, we don't shy away from hard problems.",
       },
       {
        icon: DollarSign,
        title: "We Deliver Business Impact",
        description: "90% team reductions. Eliminated subscriptions. New revenue streams. Real outcomes.",
       },
       {
        icon: Clock,
        title: "We Move Fast",
        description: "No endless discovery phases. We validate, build, and ship, then you decide what's next.",
       },
      ].map((item, i) => (
       <motion.div
        key={item.title}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: i * 0.1 }}
       >
        <Card className="card-elevated h-full">
         <CardContent className="p-6">
          <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center mb-4">
           <item.icon className="h-5 w-5 text-accent" />
          </div>
          <h3 className="font-serif text-lg font-medium text-foreground mb-2">
           {item.title}
          </h3>
          <p className="text-sm text-muted-foreground">
           {item.description}
          </p>
         </CardContent>
        </Card>
       </motion.div>
      ))}
     </div>
    </div>
   </section>

   {/* CTA */}
   <section className="bg-primary text-primary-foreground py-16 sm:py-20 lg:py-24">
    <div className="container-narrow text-center">
     <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
     >
      <h2 className="heading-large mb-4">Ready to Build Something Real?</h2>
      <p className="body-large text-primary-foreground/80 mb-8 max-w-xl mx-auto">
       Your product could be our next case study. Tell us what you're building.
      </p>
      <div className="flex flex-col sm:flex-row justify-center gap-4">
       <Button 
        variant="secondary" 
        size="lg" 
        asChild 
        className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 rounded-full"
       >
        <Link to="/contact">
         Tell Us What You're Building
         <ArrowRight className="h-4 w-4 ml-2" />
        </Link>
       </Button>
       <Button 
        variant="outline" 
        size="lg" 
        asChild 
        className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 rounded-full"
       >
        <Link to="/process">See Our Process</Link>
       </Button>
      </div>
     </motion.div>
    </div>
   </section>
  </div>
 );
}
